<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BillingPlanAPI from 'dashboard/api/billingPlan';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PlanComparisonTable from 'dashboard/components-next/Billing/PlanComparisonTable.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const { t } = useI18n();

const isLoading = ref(true);
const isSyncing = ref(false);
const isSubscribing = ref(false);

const currentPlanId = ref('free');
const status = ref('active');
const renewalLabel = ref('');
const products = ref([]);
const features = ref([]);
const billingDevMode = ref(false);

const paidProducts = computed(() =>
  products.value.filter(product => product.id !== 'free')
);

const fetchPlan = async () => {
  isLoading.value = true;
  try {
    const { data } = await BillingPlanAPI.get();
    currentPlanId.value = data.current_plan_id;
    status.value = data.status;
    renewalLabel.value = data.renewal_label;
    products.value = data.products;
    features.value = data.features;
    billingDevMode.value = data.billing_dev_mode;
  } catch (error) {
    // Ignore, banners/table simply won't render without data
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchPlan);

const openCheckout = product => {
  window.open(product.purchase_url, '_blank', 'noopener,noreferrer');
};

const subscribeDevMode = async product => {
  isSubscribing.value = true;
  try {
    await BillingPlanAPI.update(product.id);
    await fetchPlan();
    useAlert(t('PLAN_SETTINGS.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('PLAN_SETTINGS.SYNC_ERROR'));
  } finally {
    isSubscribing.value = false;
  }
};

const sync = async () => {
  isSyncing.value = true;
  try {
    await BillingPlanAPI.sync();
    await fetchPlan();
    useAlert(t('PLAN_SETTINGS.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('PLAN_SETTINGS.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('PLAN_SETTINGS.TITLE')"
        :description="$t('PLAN_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <p
          v-if="status === 'grace_period'"
          class="p-3 text-sm rounded-lg text-n-amber-11 bg-n-amber-3"
        >
          {{ t('PLAN_SETTINGS.STATUS.GRACE_PERIOD') }}
        </p>
        <p
          v-else-if="status === 'expired'"
          class="p-3 text-sm rounded-lg text-n-ruby-11 bg-n-ruby-3"
        >
          {{ t('PLAN_SETTINGS.STATUS.EXPIRED') }}
        </p>

        <div class="flex flex-col gap-1">
          <p class="text-sm text-n-slate-11">
            {{ t('PLAN_SETTINGS.CURRENT_PLAN.TITLE') }}
          </p>
          <p class="text-base font-medium text-n-slate-12">
            {{ products.find(product => product.id === currentPlanId)?.name }}
          </p>
          <p v-if="renewalLabel" class="text-sm text-n-slate-11">
            {{
              `${t('PLAN_SETTINGS.CURRENT_PLAN.RENEWAL_LABEL')}: ${renewalLabel}`
            }}
          </p>
        </div>

        <PlanComparisonTable
          v-if="!isLoading"
          :products="products"
          :features="features"
          :current-plan-id="currentPlanId"
        />

        <div
          v-for="product in paidProducts"
          :key="product.id"
          class="flex items-center gap-2"
        >
          <template v-if="currentPlanId !== product.id">
            <template v-if="product.purchase_url">
              <NextButton
                color="blue"
                type="button"
                :label="t('PLAN_SETTINGS.SUBSCRIBE_TO', { plan: product.name })"
                @click="openCheckout(product)"
              />
              <NextButton
                color="slate"
                variant="faded"
                type="button"
                :is-loading="isSyncing"
                :label="t('PLAN_SETTINGS.ALREADY_SUBSCRIBED')"
                @click="sync"
              />
            </template>
            <template v-else-if="billingDevMode">
              <NextButton
                color="blue"
                type="button"
                :is-loading="isSubscribing"
                :label="t('PLAN_SETTINGS.SUBSCRIBE_TO', { plan: product.name })"
                @click="subscribeDevMode(product)"
              />
            </template>
            <template v-else>
              <NextButton
                color="slate"
                variant="faded"
                type="button"
                disabled
                :label="t('PLAN_SETTINGS.COMING_SOON')"
              />
            </template>
          </template>
        </div>
      </section>
    </template>
  </SettingsLayout>
</template>
